import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Terminal, CheckCircle, ExternalLink, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const CybersecurityTrainingIndore = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoTitle = "Ethical Hacking & Cybersecurity Course in Indore | Ciqura Labs";
  const seoDescription = "Master advanced Ethical Hacking, VAPT, and SOC Operations at Ciqura Labs Indore. Hands-on training-linked internships with direct placement paths to Ciqur24 Bengaluru.";
  const canonicalUrl = "https://ciquralabs.ciqur24.com/programs";
  const registrationLink = "https://forms.office.com/Pages/ResponsePage.aspx?id=GIlUwqulxkW2gUlR5VePC12nZ4fz5yRFkrQsWYzu1nBUN01NTlFFS0lQOFhRNjk4MDVaQloxTkJBRS4u";

  return (
    /* 🔥 FIXED: Added flexbox column layout to guarantee footer pushes to the bottom dynamically */
    <div style={{ 
      background: 'var(--bg-primary)', 
      minHeight: '100vh', 
      paddingTop: '80px', 
      color: 'var(--text-primary)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="Cybersecurity training in Indore, Ethical Hacking course Indore, Best Cyber Security Institute in Indore, Vijay Nagar IT training, VAPT certification Indore, SOC Analyst course Indore, Cyber Security classes in Vijay Nagar, Network Security training Indore, Cyber security internship 2026, Hands-on hacking labs, Ciqura Labs Indore courses, Shagun Arcade hacking institute" />
        <link rel="canonical" href={canonicalUrl} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Advanced Cybersecurity & Ethical Hacking Masterclass",
            "description": "Industrial grade information security training covering VAPT, Threat Intelligence, and Live SOC Operations with real enterprise-grade labs in Indore.",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Ciqura Labs",
              "sameAs": "https://ciquralabs.ciqur24.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "113, First Floor, Shagun Arcade, Vijay Nagar",
                "addressLocality": "Indore",
                "addressRegion": "MP",
                "postalCode": "452010",
                "addressCountry": "IN"
              }
            },
            "educationalCredentialAwarded": "Professional Industry Certification & Ciqur24 Industrial Internship Letter"
          })}
        </script>
      </Helmet>

      {/* 🔥 FIXED: Explicitly wrapped main content block with flex: 1 to push elements cleanly */}
      <main style={{ flex: '1 0 auto' }}>
        {/* ================= HEADER HERO AREA ================= */}
        <section style={{ padding: '80px 7.6923%', textAlign: 'center', background: 'radial-gradient(circle at top, rgba(0,255,209,0.15) 0%, transparent 70%)' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0, 255, 209, 0.1)', color: 'var(--brand-primary)', padding: '8px 22px', borderRadius: '30px', fontSize: '14px', fontWeight: '700', marginBottom: '24px', border: '1px solid var(--brand-primary)' }}>
            <Shield size={16} /> FLAGSHIP CYBER DEFENSE PROGRAM 2026
          </div>
          <h1 className="display-huge" style={{ marginBottom: '24px', lineHeight: 1.1 }}>
            Advanced <span style={{ color: 'var(--brand-primary)' }}>Ethical Hacking</span> & Cyber Security Training in Indore
          </h1>
          
          <h2 className="heading-2" style={{ color: 'var(--brand-primary)', fontSize: '22px', marginBottom: '20px', fontWeight: '700' }}>
            Best Ethical Hacking & AIML Training Institute in Indore with Live SOC Labs
          </h2>

          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '950px', margin: '0 auto', fontSize: '19px', lineHeight: 1.7 }}>
            Forge a bulletproof career in information security. Train at **Ciqura Labs Indore**, the elite cybersecurity training division of live security operations enterprise **Ciqur24 Bengaluru**. Master absolute offensive penetration testing and real-time defensive infrastructure monitoring inside a dedicated hardware lab setup.
          </p>
        </section>

        {/* ================= MAIN CONTENT SECTION ================= */}
        <section style={{ padding: '40px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '50px' }} className="responsive-grid">
            
            {/* Left Column: Deep Semantic Information Layout */}
            <div>
              <article style={{ marginBottom: '50px' }}>
                <h2 className="heading-1" style={{ color: '#fff', marginBottom: '20px' }}>Indore's Only Live SOC Sandbox Training</h2>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                  Traditional computer institutes teach outdated network theories off presentations. At Ciqura Labs Vijay Nagar, we break the mold. Our students gain active, individual deployment keys to highly virtualized environments where they construct, execute, and analyze real cyberattacks and malware logic safely.
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                  By focusing directly on **Vulnerability Assessment and Penetration Testing (VAPT)** and modern **Security Operations Center (SOC)** workflows, you learn how enterprise infrastructure is actively defended against modern threats, ransomware campaigns, and systemic domain compromises.
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  Our deep tactical approach ensures you completely understand defensive topologies. This technical familiarity helps bridge the vast gap between classical academic degrees and the complex, metrics-driven operations demanded by international security firms.
                </p>
              </article>

              {/* Structured Multi-Tier Syllabus Architecture */}
              <div style={{ marginBottom: '50px' }}>
                <h3 className="heading-2" style={{ color: '#fff', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Terminal style={{ color: 'var(--brand-primary)' }} /> Rigorous Multi-Tier Training Syllabus
                </h3>
                
                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: '12px', overflow: 'hidden' }}>
                  <Accordion type="single" collapsible>
                    
                    <AccordionItem value="mod-1" style={{ borderBottom: '1px solid var(--border-subtle)', padding: '0 24px' }}>
                      <AccordionTrigger><span className="heading-3">1. Information Gathering & Reconnaissance</span></AccordionTrigger>
                      <AccordionContent style={{ padding: '10px 0 20px', color: 'var(--text-secondary)' }}>
                        <p className="body-medium" style={{ marginBottom: '12px' }}>Master advanced open-source intelligence (OSINT) workflows, network mapping methodologies, and targeted discovery phases designed to locate exposed parameters within target enterprise vectors.</p>
                        <ul style={{ paddingLeft: '20px', listStyleType: 'square', lineHeight: '1.7' }}>
                          <li>Passive and Active Information Gathering Blueprints</li>
                          <li>Advanced Port Mapping and Fingerprinting Parameters</li>
                          <li>Automated Subdomain Tracking and Directory Fuzzing</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="mod-2" style={{ borderBottom: '1px solid var(--border-subtle)', padding: '0 24px' }}>
                      <AccordionTrigger><span className="heading-3">2. Vulnerability Assessment & Web App Pentesting (VAPT)</span></AccordionTrigger>
                      <AccordionContent style={{ padding: '10px 0 20px', color: 'var(--text-secondary)' }}>
                        <p className="body-medium" style={{ marginBottom: '12px' }}>Isolate, audit, and systematically exploit systemic vulnerabilities within web applications, databases, and composite system API pathways following absolute global web security guidelines.</p>
                        <ul style={{ paddingLeft: '20px', listStyleType: 'square', lineHeight: '1.7' }}>
                          <li>OWASP Top 10 Exploitation Frameworks (SQLi, XSS, IDOR)</li>
                          <li>Advanced Interception Proxying with Burp Suite Pro</li>
                          <li>Automated Network Vulnerability Assessment Implementation</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="mod-3" style={{ borderBottom: '1px solid var(--border-subtle)', padding: '0 24px' }}>
                      <AccordionTrigger><span className="heading-3">3. SOC Operations & Threat Monitoring</span></AccordionTrigger>
                      <AccordionContent style={{ padding: '10px 0 20px', color: 'var(--text-secondary)' }}>
                        <p className="body-medium" style={{ marginBottom: '12px' }}>Pivot seamlessly into Defensive Security operations. Learn how to configure modern threat logging pipelines, manage incident responses, and triage alerts within enterprise settings.</p>
                        <ul style={{ paddingLeft: '20px', listStyleType: 'square', lineHeight: '1.7' }}>
                          <li>SIEM Management Systems Setup & Operational Live Dashboards</li>
                          <li>Packet Inspection Protocols, Intrusion Analysis & Network Auditing</li>
                          <li>Malware Behavior Diagnostics and Tactical System Hardening</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>

                  </Accordion>
                </div>
              </div>

              {/* Industrial Toolsets Array Box */}
              <div style={{ marginBottom: '40px' }}>
                <h3 className="heading-2" style={{ color: '#fff', marginBottom: '20px' }}>Enterprise Tools You Will Command</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {['Kali Linux', 'Burp Suite Pro', 'Splunk SIEM', 'Wireshark', 'Metasploit Framework', 'Nmap', 'OWASP ZAP', 'Maltego', 'Snort IDS', 'Azure Defender'].map((tool, idx) => (
                    <span key={idx} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--brand-primary)', padding: '10px 20px', fontWeight: '600', borderRadius: '4px', fontSize: '14px' }}>
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Admission Call-To-Action Sandbox Box */}
            <div style={{ position: 'sticky', top: '100px', height: 'fit-content' }}>
              <div style={{ background: 'var(--bg-secondary)', border: '2px solid var(--brand-primary)', padding: '40px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'inline-block', background: 'var(--brand-primary)', color: '#000', padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', marginBottom: '16px' }}>
                  BATCH ADMISSIONS OPEN • 2026
                </div>
                <h3 className="heading-1" style={{ marginBottom: '12px' }}>Secure Your Lab Workspace</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                  To preserve personal instructor access, individual cohort sizes inside our Vijay Nagar lab are strictly limited.
                </p>

                <ul style={{ textAlign: 'left', marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '14px' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><CheckCircle size={16} style={{ color: 'var(--brand-primary)' }} /> 80% Live Lab-Based Curriculum</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><CheckCircle size={16} style={{ color: 'var(--brand-primary)' }} /> Direct Corporate Internship Track</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><CheckCircle size={16} style={{ color: 'var(--brand-primary)' }} /> Weekly Mock Technical Interviews</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><CheckCircle size={16} style={{ color: 'var(--brand-primary)' }} /> Official Training Letter from Ciqur24</li>
                </ul>

                <Button className="btn-primary" onClick={() => window.open(registrationLink, '_blank', 'noopener,noreferrer')} style={{ width: '100%', height: '55px', fontSize: '16px', fontWeight: '700' }}>
                  Apply for Admission Track <ExternalLink size={18} style={{ marginLeft: '8px' }} />
                </Button>

                <p style={{ marginTop: '20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Call Admissions Desk: <a href="tel:+919303998387" style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: 'bold' }}>+91 9303998387</a>
                </p>
              </div>
            </div>

          </div>
        </section>
      </main>

      {/* ================= COMPREHENSIVE EXPERT FOOTER BLUEPRINT ================= */}
      <footer style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-subtle)', padding: '60px 7.6923% 30px', marginTop: 'auto', flexShrink: 0 }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '40px', marginBottom: '40px' }}>
            
            {/* Column 1: Company Credentials Statement */}
            <div>
              <h4 className="heading-3" style={{ color: '#fff', marginBottom: '16px' }}>Ciqura Labs</h4>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '14px' }}>
                Ciqura Labs is the specialized training arm of **Ciqur24 LLP**, providing industry-grade IT & Cybersecurity certifications in Indore. We establish robust practical benchmarks designed to challenge and prepare students for technical corporate roles.
              </p>
            </div>

            {/* Column 2: Structured Navigation Vectors */}
            <div>
              <h4 className="heading-3" style={{ color: '#fff', marginBottom: '16px' }}>Quick Links</h4>
              <ul style={{ listStyleType: 'none', paddingLeft: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
                <li><a href="/about" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>About Ciqura Labs</a></li>
                <li><a href="/programs" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>All Training Programs</a></li>
                <li><a href="/internship" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Bengaluru Internship</a></li>
                <li><a href="/contact" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Contact Support</a></li>
              </ul>
            </div>

            {/* Column 3: High Ranking Keyword Verticals */}
            <div>
              <h4 className="heading-3" style={{ color: '#fff', marginBottom: '16px' }}>Our Programs</h4>
              <ul style={{ listStyleType: 'none', paddingLeft: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
                <li><a href="/programs" style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: '600' }}>Ethical Hacking Indore</a></li>
                <li><a href="/programs/ai-machine-learning" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>AI & Machine Learning</a></li>
                <li><a href="/programs/grc-professional" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>GRC Professional</a></li>
                <li><a href="/programs/full-stack-web-development" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Full Stack Web Dev</a></li>
                <li><a href="/programs/microsoft-365-cloud-admin" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Microsoft Azure Cloud</a></li>
              </ul>
            </div>

            {/* Column 4: Physical Location Audit Context */}
            <div>
              <h4 className="heading-3" style={{ color: '#fff', marginBottom: '16px' }}>Visit Our Lab</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <MapPin size={18} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                  <span>113, First Floor, Shagun Arcade,<br />Above Apna Sweets, Vijay Nagar,<br />Indore, MP 452010</span>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <Phone size={16} style={{ color: 'var(--brand-primary)' }} />
                  <a href="tel:+919303998387" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>+91 9303998387</a>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <Mail size={16} style={{ color: 'var(--brand-primary)' }} />
                  <a href="mailto:training@ciqur24.com" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>training@ciqur24.com</a>
                </div>
              </div>
            </div>

          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border-subtle)', margin: '30px 0' }} />

          {/* Legal / Corporate Registration Meta Elements */}
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', fontSize: '13px', color: 'var(--text-muted)' }}>
            <span>© 2026 Ciqura Labs (Training Division of Ciqur24 LLP).</span>
            <span>Registered as Ciqur24 LLP. Active corporate delivery operations located across Bengaluru, Gandhinagar, and Indore.</span>
          </div>

        </div>
      </footer>

    </div>
  );
};

export default CybersecurityTrainingIndore;
